import { Component } from '@theme/component';
import { debounce, isMobileBreakpoint } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ["wrapper", "content"];

  /** @type {number} */
  #lastWidth = 0;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {{ cancel: () => void, current: number } | null} */
  #animation = null;

  /** @type {boolean} */
  #isPaused = false;

  /** @type {{x:number,y:number} | null} */
  #touchStartPos = null;
  // #isPaused = false;

  connectedCallback() {
    super.connectedCallback();
    
    const { content } = this.refs;
    const firstChild = 
      content.firstElementChild;

    if (!(firstChild instanceof HTMLElement)) return;
    if (firstChild.children.length === 0
    )
      return;

    if(this.closest(".spotlight-carousel-section ")){
      if(firstChild.children.length <= 4) return;
    }

    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    this.#lastWidth = this.offsetWidth;

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#handleResize();
      });
      this.#resizeObserver.observe(this);
    } else {
      window.addEventListener("resize", this.#handleResize);
    }

    if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      this.addEventListener("pointerenter", (event) => {
      if (event.pointerType !== "mouse") return;
      this.#slowDown();
    });
      this.addEventListener("pointerleave", (event) => {
      if (event.pointerType !== "mouse") return;
      this.#speedUp();
    });
    this.addEventListener("touchmove", this.#clicked);
    } else {
      this.addEventListener('pointerdown', this.#onPointerDownMobile, { passive: true });
      this.addEventListener('pointerup', this.#onPointerUpMobile, { passive: true });
      this.addEventListener('pointercancel', this.#onPointerCancelMobile, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    window.removeEventListener("resize", this.#handleResize);

    if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      this.removeEventListener("pointerenter", this.#slowDown);
      this.removeEventListener("pointerleave", this.#speedUp);
    } else {
      this.removeEventListener('pointerdown', this.#onPointerDownMobile);
      this.removeEventListener('pointerup', this.#onPointerUpMobile);
      this.removeEventListener('pointercancel', this.#onPointerCancelMobile);
    }
  }

  triggerSpeedUp() {
    this.#speedUp();
    this.#isPaused = false;
  }

  triggerSlowDown() {
    if (this.#isPaused) return;
    this.#clicked();
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  // #animation = null;

  #clicked = debounce(() => {
    if (this.#animation || this.#isPaused) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      duration: 0,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });

    this.#isPaused = true;
  }, 0);

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => {
        animation.updatePlaybackRate(value);
      },
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #startSlowDownImmediate() {
    if (this.#animation) return;
  
    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;
  
    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: animation.playbackRate || 1,
      to: 0,
      onUpdate: (value) => {
        animation.playbackRate = value;
      },
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  #speedUp() {
    if (typeof this.#slowDown.cancel === 'function') {
      this.#slowDown.cancel();
    }
  
    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;
  
    const from = this.#animation?.current ?? animation.playbackRate ?? 0;
    this.#animation?.cancel();
  
    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => {
        animation.playbackRate = value;
      },
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  /**
   * @param {PointerEvent} ev
   */
  #onPointerDownMobile = (ev) => {
    this.#touchStartPos = { x: ev.clientX, y: ev.clientY };
  };

  /**
   * @param {PointerEvent} ev
   */
  #onPointerUpMobile = (ev) => {
    if (!this.#touchStartPos) return;

    const dx = Math.abs(ev.clientX - this.#touchStartPos.x);
    const dy = Math.abs(ev.clientY - this.#touchStartPos.y);
    this.#touchStartPos = null;

    if (dx > 10 || dy > 10) return;

    if (this.#isPaused) {
      this.#speedUp();
      this.#isPaused = false;
    } else {
      this.#startSlowDownImmediate();
      this.#isPaused = true;
    }
  };

  #onPointerCancelMobile = () => {
    this.#touchStartPos = null;
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty("--marquee-speed", `${value}s`);
  }

  #calculateSpeed() {
    const speedFactorDesktop = Number(this.getAttribute('data-speed-factor')) || 1;
    const speedFactorMobile = Number(this.getAttribute('data-speed-factor-mobile')) || 1;
    const speedFactor = isMobileBreakpoint() ? speedFactorMobile : speedFactorDesktop;

    const marqueeWidth = this.offsetWidth || 0;
    return Math.ceil(marqueeWidth / speedFactor / 2);
  }

  #handleResize = debounce(() => {
    const newWidth = this.offsetWidth;
    if (newWidth === this.#lastWidth) return;
    this.#lastWidth = newWidth;

    const { content } = this.refs;
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = content.children.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations?.() || [];
    if (!animations.length) return;

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (
      this.refs.content.cloneNode(true)
    );

    clone.setAttribute("aria-hidden", "true");
    clone.removeAttribute("ref");

    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const wrapper = content.firstElementChild;

    if (!wrapper) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = wrapper.cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;

    for (let i = 0; i < numberOfCopies; i++) {
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { content } = this.refs;
    const marqueeWidth = this.offsetWidth || 0;
    const marqueeRepeatedItemWidth =
      content.firstElementChild instanceof HTMLElement
        ? content.firstElementChild.offsetWidth
        : 1;

    return marqueeRepeatedItemWidth === 0
      ? 1
      : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({
  from,
  to,
  duration,
  onUpdate,
  easing = (t) => t * t * (3 - 2 * t),
  onComplete,
}) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === "function") {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get("marquee-component")) {
  customElements.define("marquee-component", MarqueeComponent);
}
